import { create } from 'zustand';
import { ERX_ENABLED } from '../config/developmentPreviews';

export type NotificationSeverity = 'info' | 'success' | 'warning' | 'critical';
export type NotificationSource = 'patient' | 'appointment' | 'prescription' | 'billing' | 'documents' | 'telemed' | 'system';

export type NotificationItem = {
  id: string;
  title: string;
  message: string;
  source: NotificationSource;
  severity: NotificationSeverity;
  read: boolean;
  createdAt: string;
  href?: string;
};

type NotificationDraft = {
  title: string;
  message: string;
  source?: NotificationSource;
  severity?: NotificationSeverity;
  href?: string;
};

type NotificationsState = {
  items: NotificationItem[];
  hydrated: boolean;
  addNotification: (draft: NotificationDraft) => void;
  markRead: (id: string) => void;
  markAllRead: () => void;
  clearAll: () => void;
  addMockEvent: () => void;
  hydrate: () => void;
};

const LOCAL_STORAGE_KEY = 'neoemr.notifications.v2';
const MAX_NOTIFICATIONS = 80;

function generateId() {
  return `ntf_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function loadStoredNotifications(): NotificationItem[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((item) => sanitizeNotification(item))
      .filter(Boolean)
      .slice(0, MAX_NOTIFICATIONS) as NotificationItem[];
  } catch {
    return [];
  }
}

function persistNotifications(items: NotificationItem[]) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(items.slice(0, MAX_NOTIFICATIONS)));
  } catch {
    // no-op
  }
}

function sanitizeNotification(value: any): NotificationItem | null {
  if (!value || typeof value !== 'object') return null;
  const title = String(value.title || '').trim();
  const message = String(value.message || '').trim();
  if (!title || !message) return null;
  return {
    id: String(value.id || generateId()),
    title,
    message,
    source: normalizeSource(value.source),
    severity: normalizeSeverity(value.severity),
    read: Boolean(value.read),
    createdAt: String(value.createdAt || new Date().toISOString()),
    href: typeof value.href === 'string' ? value.href : undefined,
  };
}

function normalizeSource(source: unknown): NotificationSource {
  const val = String(source || '').toLowerCase();
  if (val === 'patient' || val === 'appointment' || val === 'prescription' || val === 'billing' || val === 'documents' || val === 'telemed') {
    return val;
  }
  return 'system';
}

function normalizeSeverity(severity: unknown): NotificationSeverity {
  const val = String(severity || '').toLowerCase();
  if (val === 'success' || val === 'warning' || val === 'critical') return val;
  return 'info';
}

function randomMockEvent(): NotificationDraft {
  const candidates: NotificationDraft[] = [
    {
      title: 'Patient note updated',
      message: 'Patient note changes were saved.',
      source: 'patient',
      severity: 'info',
    },
    // Prescription/e-Rx is not built yet; only surface this mock affordance when the
    // feature flag is on, so users never click through to a dead /prescriptions route.
    ...(ERX_ENABLED
      ? [
          {
            title: 'Prescription updated',
            message: 'Prescription changes were saved.',
            source: 'prescription' as const,
            severity: 'warning' as const,
            href: '/prescriptions',
          },
        ]
      : []),
    {
      title: 'Billing updated',
      message: 'Billing changes were saved.',
      source: 'billing',
      severity: 'warning',
      href: '/billing',
    },
    {
      title: 'Patient document uploaded',
      message: 'Patient document changes were saved.',
      source: 'documents',
      severity: 'success',
    },
    {
      title: 'Telemedicine updated',
      message: 'Telemedicine changes were saved.',
      source: 'telemed',
      severity: 'info',
      href: '/telemedicine',
    },
  ];
  return candidates[Math.floor(Math.random() * candidates.length)];
}

export const useNotificationsStore = create<NotificationsState>((set, get) => ({
  items: [],
  hydrated: false,
  hydrate: () => {
    const current = get();
    if (current.hydrated) return;
    const stored = loadStoredNotifications();
    const initial = stored.length ? stored : [];
    set({ items: initial, hydrated: true });
    persistNotifications(initial);
  },
  addNotification: (draft) => {
    const next: NotificationItem = {
      id: generateId(),
      title: String(draft.title || 'Notification').trim(),
      message: String(draft.message || '').trim(),
      source: normalizeSource(draft.source),
      severity: normalizeSeverity(draft.severity),
      read: false,
      createdAt: new Date().toISOString(),
      href: draft.href,
    };
    set((state) => {
      const items = [next, ...state.items].slice(0, MAX_NOTIFICATIONS);
      persistNotifications(items);
      return { items };
    });
  },
  markRead: (id) => {
    set((state) => {
      const items = state.items.map((item) => (item.id === id ? { ...item, read: true } : item));
      persistNotifications(items);
      return { items };
    });
  },
  markAllRead: () => {
    set((state) => {
      const items = state.items.map((item) => ({ ...item, read: true }));
      persistNotifications(items);
      return { items };
    });
  },
  clearAll: () => {
    set({ items: [] });
    persistNotifications([]);
  },
  addMockEvent: () => {
    get().addNotification(randomMockEvent());
  },
}));

function parsePatientId(path: string) {
  const match = path.match(/\/patients\/([^/?]+)/i);
  return match?.[1] || null;
}

function shouldSkipPath(path: string) {
  const normalized = path.toLowerCase();
  return (
    normalized.includes('/notes/ai/') ||
    normalized.includes('/ai-summary') ||
    normalized.includes('/storage/presign') ||
    normalized.includes('/search') ||
    normalized.includes('/health')
  );
}

export function emitNotificationFromApi(path: string, method: string, ok: boolean, status: number) {
  const normalizedMethod = String(method || 'GET').toUpperCase();
  if (normalizedMethod === 'GET') return;
  if (shouldSkipPath(path)) return;

  const store = useNotificationsStore.getState();
  if (!store.hydrated) {
    store.hydrate();
  }

  const patientId = parsePatientId(path);
  const isError = !ok || status >= 400;

  if (isError) {
    store.addNotification({
      title: 'Action failed',
      message: 'The requested action could not be completed.',
      source: 'system',
      severity: 'warning',
    });
    return;
  }

  const lower = path.toLowerCase();
  if (lower.includes('/patients/') && lower.includes('/notes')) {
    store.addNotification({
      title: normalizedMethod === 'POST' ? 'Patient note created' : 'Patient note updated',
      message: 'Patient note changes were saved.',
      source: 'patient',
      severity: 'info',
      href: patientId ? `/patients/${patientId}` : '/patients',
    });
    return;
  }

  if (lower.includes('/patients/') && lower.includes('/documents')) {
    store.addNotification({
      title: normalizedMethod === 'DELETE' ? 'Patient document removed' : 'Patient document uploaded',
      message: 'Patient document changes were saved.',
      source: 'documents',
      severity: normalizedMethod === 'DELETE' ? 'info' : 'success',
      href: patientId ? `/patients/${patientId}` : '/patients',
    });
    return;
  }

  if (lower.startsWith('/appointments')) {
    store.addNotification({
      title: 'Appointment updated',
      message: 'Appointment changes were saved.',
      source: 'appointment',
      severity: 'success',
      href: '/appointments',
    });
    return;
  }

  if (ERX_ENABLED && lower.startsWith('/prescriptions')) {
    store.addNotification({
      title: 'Prescription updated',
      message: 'Prescription changes were saved.',
      source: 'prescription',
      severity: 'success',
      href: '/prescriptions',
    });
    return;
  }

  if (lower.startsWith('/billing')) {
    store.addNotification({
      title: 'Billing updated',
      message: 'Billing changes were saved.',
      source: 'billing',
      severity: 'success',
      href: '/billing',
    });
    return;
  }

  if (lower.startsWith('/telemed')) {
    store.addNotification({
      title: 'Telemedicine updated',
      message: 'Telemedicine changes were saved.',
      source: 'telemed',
      severity: 'info',
      href: '/telemedicine',
    });
    return;
  }

  if (lower.startsWith('/patients/')) {
    store.addNotification({
      title: 'Patient record updated',
      message: 'Patient record changes were saved.',
      source: 'patient',
      severity: 'success',
      href: patientId ? `/patients/${patientId}` : '/patients',
    });
    return;
  }

  store.addNotification({
    title: 'System activity captured',
    message: 'Changes were saved successfully.',
    source: 'system',
    severity: 'info',
  });
}
